import { useState, useEffect, useMemo, useRef } from "preact/hooks"
import { Button } from "../../../../components/ui/button"
import { Badge } from "../../../../components/ui/badge"
import { Input } from "../../../../components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table"
import { PromotionEditForm } from "../../promo_creation/components/promotion_forms"
import {
  getPromotionDeductions,
  PromotionDeductionResponse,
  DeductionSearchResult,
  createPromotionMatch,
  searchDeductions,
} from "../../../../api/deduction"
import { exactCurrencyFormatter } from "../../../../utils"
import { parseISO, format } from "date-fns"
import {
  Loader2,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  Edit3,
  Forward,
  Receipt,
  Search,
  Plus,
  Paperclip,
  BarChart3,
  Rows3,
  Link2,
  Trash2,
  History,
} from "lucide-react"
import { navigate } from "wouter-preact/use-location"
import { CREATE_PROMOTION_PATH } from "../../routes"
import { Promotion, PromotionLine } from "../../promo_creation/types"
import { PromotionFiles } from "./promotion_files"
import { PromotionHistoryTab } from "./promotion_history_tab"
import { usePromotionalContextAPI } from "../../promo_creation/hooks/use_promotional_context_api"
import { PromotionInsightsTab } from "./effectiveness/PromotionInsightsTab"
import { PromotionLinesTable } from "./PromotionLinesTable"
import { toast } from "../../../../components/ui/use-toast"
import { api_fetch } from "../../../../api/client"
import { UserState } from "@/auth/user"

interface EnhancedPromotionEditModalProps {
  promotion: Promotion | null
  isOpen: boolean
  onClose: () => void
  onSave: (promotion: Promotion) => void
  /** Called with the newly created promotion after a copy-forward from this modal (e.g. to open it). */
  onCopyForwardComplete?: (promotion: Promotion) => void
  /**
   * Deletes the promotion and refreshes the caller's list. Optional only so surfaces with nothing
   * to refresh can omit it — where it is passed, the modal offers Delete, which is the only place
   * a planner reaches from the calendar grid or the board (the trash icon lives in List view).
   */
  onDelete?: (promotionId: string) => void | Promise<void>
}

/** Default copy-forward target: the year after the promotion's own year (fiscal year or start date). */
export function default_copy_forward_target_year(
  promotion: Pick<Promotion, "fiscal_year" | "start_date">,
  now: Date = new Date(),
): number {
  const source_year = promotion.fiscal_year ?? (promotion.start_date ? Number(promotion.start_date.slice(0, 4)) : undefined)
  return (source_year && Number.isFinite(source_year) ? source_year : now.getFullYear()) + 1
}

export function EnhancedPromotionEditModal({ promotion, isOpen, onClose, onSave, onCopyForwardComplete, onDelete }: EnhancedPromotionEditModalProps) {
  const [deductions, setDeductions] = useState<PromotionDeductionResponse[]>([])
  const [searchResults, setSearchResults] = useState<DeductionSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<"edit" | "deductions" | "files" | "insights" | "lines" | "history">("edit")
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [filesCount, setFilesCount] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [lines, setLines] = useState<PromotionLine[]>([])
  const [linesLoading, setLinesLoading] = useState(false)
  // Local copy of the promotion so edits (e.g. planned amount) are reflected immediately across tabs
  // (notably the Insights tab) without waiting for the parent list to refetch.
  const [currentPromotion, setCurrentPromotion] = useState<Promotion | null>(promotion)
  const { listPromotionFiles, listPromotionLines } = usePromotionalContextAPI()
  const opened_promotion_id_ref = useRef<string | null>(null)
  // Brokers can't reach the deduction endpoints (server allowlist), so hide that tab for them.
  const current_user = UserState.use()
  const is_broker = current_user?.role === "broker"

  async function loadFilesCount() {
    if (!promotion?.id) return

    try {
      const files = await listPromotionFiles(promotion.id)
      setFilesCount(files.length)
    } catch (err) {
      console.error("Failed to load files count:", err)
      setFilesCount(0)
    }
  }

  async function loadLines() {
    if (!promotion?.id) return

    setLinesLoading(true)
    try {
      setLines(await listPromotionLines(promotion.id))
    } catch (err) {
      console.error("Failed to load promotion lines:", err)
      setLines([])
    } finally {
      setLinesLoading(false)
    }
  }

  // Keep the local promotion copy in sync when the parent opens a different promotion.
  useEffect(() => {
    setCurrentPromotion(promotion)
  }, [promotion])

  useEffect(() => {
    if (!isOpen) {
      opened_promotion_id_ref.current = null
      return
    }
    if (!promotion?.id) return

    const is_new_modal_session = opened_promotion_id_ref.current !== promotion.id
    if (is_new_modal_session) {
      opened_promotion_id_ref.current = promotion.id
      setActiveTab("edit")
      if (!is_broker) {
        loadDeductions({ auto_switch_tab: true })
      }
      loadFilesCount()
      loadLines()
    }
  }, [isOpen, promotion?.id])

  // Debounce search term and perform global search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 150) // 150ms debounce like the promotion selector

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Perform global deduction search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      performGlobalSearch(debouncedSearchTerm)
    } else {
      setSearchResults([])
    }
  }, [debouncedSearchTerm])

  async function performGlobalSearch(query: string) {
    setSearchLoading(true)
    try {
      const results = await searchDeductions({ q: query })
      setSearchResults(results)
    } catch (err) {
      setSearchResults([])
    } finally {
      setSearchLoading(false)
    }
  }

  async function loadDeductions(options?: { auto_switch_tab?: boolean }) {
    if (!promotion?.id) return

    setLoading(true)
    setError(null)

    try {
      const data = await getPromotionDeductions(promotion.id)
      setDeductions(data)

      if (options?.auto_switch_tab && data.length > 0) {
        setActiveTab("deductions")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load deductions")
    } finally {
      setLoading(false)
    }
  }

  function handleClose() {
    setDeductions([])
    setSearchResults([])
    setError(null)
    setActiveTab("edit")
    setSearchTerm("")
    setDebouncedSearchTerm("")
    setFilesCount(0)
    setShowCopyForward(false)
    setCopyForwardTargetYear(null)
    setCopyForwardFiles(false)
    onClose()
  }

  // Get matched deduction IDs for filtering search results
  const matchedDeductionIds = useMemo(() => {
    return new Set(deductions.map(d => d.deduction.id))
  }, [deductions])

  // Separate search results into matched and unmatched
  const { matchedSearchResults, unmatchedSearchResults } = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return { matchedSearchResults: [], unmatchedSearchResults: [] }
    }

    const matched: DeductionSearchResult[] = []
    const unmatched: DeductionSearchResult[] = []

    searchResults.forEach(deduction => {
      if (matchedDeductionIds.has(deduction.id)) {
        matched.push(deduction)
      } else {
        unmatched.push(deduction)
      }
    })

    return { matchedSearchResults: matched, unmatchedSearchResults: unmatched }
  }, [searchResults, matchedDeductionIds, debouncedSearchTerm])

  function getMatchIcon(matched: boolean) {
    return matched ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />
  }

  function getMatchBadgeVariant(matched: boolean) {
    return matched ? "default" : "destructive"
  }

  function formatMatchScore(score: number) {
    return `${Math.round(score * 100)}%`
  }

  function getMatchScoreColor(score: number) {
    if (score >= 0.8) return "text-green-600"
    if (score >= 0.6) return "text-yellow-600"
    return "text-red-600"
  }

  async function createMatch(deductionId: string) {
    if (!promotion?.id) return

    try {
      const response = await createPromotionMatch({
        promotion_id: promotion.id,
        deduction_id: deductionId,
      })

      if (response.success) {
        // Reload the deductions to show the new match
        await loadDeductions()

        // Remove from search results since it's now matched
        setSearchResults(prev => prev.filter(d => d.id !== deductionId))

        // Clear the search field so the matched deduction appears in the Matched Deductions section
        setSearchTerm("")
        setDebouncedSearchTerm("")
      } else {
        setError(response.message)
      }
    } catch (err) {
      setError("Failed to create match. Please try again.")
    }
  }

  // Persist an edit and reflect it locally right away so other tabs (e.g. Insights) see the change.
  async function handleEditSave(updated: Promotion) {
    setCurrentPromotion(updated)
    await onSave(updated)
    // The per-product rows are rewritten on save, so the Lines tab reads them again.
    await loadLines()
  }

  // Copy-forward panel state (copy this promotion into a target year from the detail view).
  const [showCopyForward, setShowCopyForward] = useState(false)
  const [copyForwardTargetYear, setCopyForwardTargetYear] = useState<number | null>(null)
  const [copyForwardFiles, setCopyForwardFiles] = useState(false)
  const [isCopyingForward, setIsCopyingForward] = useState(false)

  async function handleCopyForward() {
    if (!active_promotion.id) return
    const target_year = copyForwardTargetYear ?? default_copy_forward_target_year(active_promotion)
    setIsCopyingForward(true)
    try {
      const res = await api_fetch<Promotion[]>("/promotions/copy-forward", {
        method: "POST",
        body: {
          target_year,
          promotion_ids: [active_promotion.id],
          copy_files: copyForwardFiles,
        },
      })
      if (!res.ok) {
        toast({ title: "Copy forward failed", description: res.error.message, variant: "destructive" })
        return
      }
      const created = res.value.data?.[0]
      if (!created) {
        toast({ title: "Copy forward failed", description: "No promotion was created.", variant: "destructive" })
        return
      }
      toast({
        title: "Promotion copied forward",
        description: `${created.name}${created.remote_promo_id ? ` (${created.remote_promo_id})` : ""} created for ${target_year}.`,
      })
      setShowCopyForward(false)
      onCopyForwardComplete?.(created)
    } finally {
      setIsCopyingForward(false)
    }
  }

  // Deleting is a soft delete server-side (is_active = false), but it is still the destructive
  // action in this modal, so it confirms by name rather than firing on a single click.
  async function handleDelete() {
    const promotion_id = active_promotion.id
    if (!promotion_id || !onDelete) return
    if (!window.confirm(`Delete "${active_promotion.name}"?\n\nIt will be removed from the calendar.`)) return

    setIsDeleting(true)
    try {
      await onDelete(promotion_id)
      onClose()
    } finally {
      setIsDeleting(false)
    }
  }

  // Copy a shareable deep link to this promotion's detail popup.
  async function handleCopyLink() {
    if (!active_promotion.id) return
    const url = `${window.location.origin}/trade-promotion-management?promotion=${active_promotion.id}`
    try {
      await navigator.clipboard.writeText(url)
      toast({ title: "Link copied", description: "A link to this promotion was copied to your clipboard." })
    } catch (err) {
      toast({ title: "Could not copy link", description: url, variant: "destructive" })
    }
  }

  if (!promotion) return null

  const active_promotion = currentPromotion ?? promotion
  const hasDeductions = deductions.length > 0
  const hasFiles = filesCount > 0
  const hasLines = lines.length > 0

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={handleClose}>
      <div
        className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col relative z-[10000]"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-medium text-gray-900">Edit Promotion</h3>
              <Badge variant="outline" className="text-sm">
                {active_promotion.name}
              </Badge>
              <Badge variant="secondary" className="text-sm" title="Where the plan is in its approval">
                {active_promotion.stage || "Pending"}
              </Badge>
              {hasDeductions && (
                <Badge variant="secondary" className="text-sm">
                  {deductions.length} deduction{deductions.length !== 1 ? "s" : ""} matched
                </Badge>
              )}
              {hasFiles && (
                <Badge variant="secondary" className="text-sm">
                  {filesCount} file{filesCount !== 1 ? "s" : ""}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {active_promotion.id && (
                <Button
                  onClick={() => navigate(`${CREATE_PROMOTION_PATH}?from=${active_promotion.id}`)}
                  variant="outline"
                  title="Start a new promotion from a copy of this one">
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </Button>
              )}
              {active_promotion.id && (
                <Button
                  onClick={() => setShowCopyForward(!showCopyForward)}
                  variant="outline"
                  title="Copy this promotion into a future year">
                  <Forward className="w-4 h-4 mr-2" />
                  Copy forward
                </Button>
              )}
              {active_promotion.id && (
                <Button onClick={handleCopyLink} variant="outline" title="Copy a shareable link to this promotion">
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy link
                </Button>
              )}
              {active_promotion.id && onDelete && (
                <Button
                  onClick={handleDelete}
                  variant="outline"
                  disabled={isDeleting}
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  title="Delete this promotion">
                  {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete
                </Button>
              )}
              <Button onClick={handleClose} variant="outline">
                Close
              </Button>
            </div>
          </div>
          {showCopyForward && (
            <div className="mt-4 flex flex-wrap items-end gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
              <label className="text-xs font-medium text-gray-600">
                Target year
                <Input
                  type="number"
                  className="mt-1 w-28"
                  value={copyForwardTargetYear ?? default_copy_forward_target_year(active_promotion)}
                  onInput={(event: Event) => setCopyForwardTargetYear(Number((event.target as HTMLInputElement).value))}
                />
              </label>
              <label className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={copyForwardFiles}
                  onChange={(event) => setCopyForwardFiles((event.target as HTMLInputElement).checked)}
                />
                Copy files
              </label>
              <Button onClick={handleCopyForward} disabled={isCopyingForward}>
                {isCopyingForward ? "Copying..." : "Copy promotion"}
              </Button>
              <p className="basis-full text-xs text-gray-500">
                Dates, name, and description shift to the target year. The copy starts in Pending.
              </p>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "edit"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("edit")}>
              <Edit3 className="w-4 h-4 inline mr-2" />
              Edit Promotion
            </button>
            {!is_broker && (
              <button
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "deductions"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("deductions")}>
                <Receipt className="w-4 h-4 inline mr-2" />
                Matched Deductions
                {hasDeductions && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {deductions.length}
                  </Badge>
                )}
              </button>
            )}
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "insights"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("insights")}
              title="Analyze promotion spend and performance">
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Insights
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "lines"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("lines")}
              title="Per-product line items imported for this promotion">
              <Rows3 className="w-4 h-4 inline mr-2" />
              Lines
              {hasLines && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {lines.length}
                </Badge>
              )}
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "files"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("files")}
              title="Upload and manage files for this promotion">
              <Paperclip className="w-4 h-4 inline mr-2" />
              Files
              {hasFiles && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {filesCount}
                </Badge>
              )}
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "history"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("history")}
              title="Who created and changed this promotion, and what changed">
              <History className="w-4 h-4 inline mr-2" />
              History
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === "edit" ? (
            <div className="p-6">
              <PromotionEditForm
                promotion={active_promotion}
                lines={lines}
                linesLoading={linesLoading}
                onSave={handleEditSave}
                onCancel={handleClose}
              />
            </div>
          ) : activeTab === "insights" ? (
            <PromotionInsightsTab
              promotion={active_promotion}
              on_switch_tab={setActiveTab}
            />
          ) : activeTab === "lines" ? (
            <div className="p-6">
              <PromotionLinesTable lines={lines} loading={linesLoading} />
            </div>
          ) : activeTab === "files" ? (
            <div className="p-6">
              {active_promotion.id ? (
                <PromotionFiles promotionId={active_promotion.id} onFilesChange={loadFilesCount} />
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>Cannot upload files to a promotion without an ID.</p>
                </div>
              )}
            </div>
          ) : activeTab === "history" ? (
            <div className="p-6">
              {active_promotion.id ? (
                <PromotionHistoryTab promotion_id={active_promotion.id} />
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>History is available after the promotion is saved.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Search Bar */}
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search deductions by invoice, PO, customer, category, or description..."
                    value={searchTerm}
                    onChange={e => setSearchTerm((e.target as HTMLInputElement).value)}
                    className="pl-10 h-10"
                  />
                </div>
                {debouncedSearchTerm && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Found {searchResults.length} deductions</span>
                    {unmatchedSearchResults.length > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-blue-600 font-medium">
                          {unmatchedSearchResults.length} available to match
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Deductions Content */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading deductions...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-red-600 mb-4">{error}</p>
                    <Button onClick={() => loadDeductions()} variant="outline">
                      Try Again
                    </Button>
                  </div>
                </div>
              ) : !debouncedSearchTerm && deductions.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center text-gray-500">
                    <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg mb-2">No deductions found</p>
                    <p className="text-sm">This promotion doesn't have any matched deductions yet.</p>
                    <p className="text-sm mt-2">Use the search bar above to find and match deductions.</p>
                  </div>
                </div>
              ) : debouncedSearchTerm && searchResults.length === 0 && !searchLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg mb-2">No matching deductions</p>
                    <p className="text-sm">Try adjusting your search terms.</p>
                    <Button variant="outline" onClick={() => setSearchTerm("")} className="mt-4">
                      Clear Search
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Existing Matches */}
                  {deductions.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Matched Deductions</h3>
                        <Badge variant="secondary" className="text-sm">
                          {deductions.length} matched
                        </Badge>
                      </div>
                      <div className="overflow-auto border border-gray-200 rounded-lg shadow-sm">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead className="font-semibold text-gray-700 pl-6">Invoice</TableHead>
                              <TableHead className="font-semibold text-gray-700">Date</TableHead>
                              <TableHead className="font-semibold text-gray-700">Amount</TableHead>
                              <TableHead className="font-semibold text-gray-700">Match Type</TableHead>
                              <TableHead className="font-semibold text-gray-700">Matched Data</TableHead>
                              <TableHead className="font-semibold text-gray-700">Matched By</TableHead>
                              <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {deductions.map(item => (
                              <TableRow key={item.deduction.id} className="hover:bg-gray-50">
                                <TableCell className="py-4 pl-6">
                                  <div className="space-y-1">
                                    <div className="font-medium text-gray-900">{item.deduction.invoice_number}</div>
                                    <div className="text-sm text-gray-500">
                                      {item.deduction.retailer_name || "Unknown Retailer"}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="py-4">
                                  <span className="text-gray-900">
                                    {format(parseISO(item.deduction.invoice_date), "MMM d, yyyy")}
                                  </span>
                                </TableCell>
                                <TableCell className="py-4">
                                  <div className="space-y-1">
                                    <div className="font-medium text-gray-900">
                                      {exactCurrencyFormatter(Math.abs(item.split_amount ?? item.deduction.invoice_amount))}
                                    </div>
                                    {item.split_amount != null && (
                                      <div className="text-xs text-gray-500">
                                        of {exactCurrencyFormatter(Math.abs(item.deduction.invoice_amount))} total
                                      </div>
                                    )}
                                    {!!item.deduction.discount_amount && (
                                      <div className="text-sm text-gray-500">
                                        Discount: {exactCurrencyFormatter(item.deduction.discount_amount)}
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="py-4">
                                  <Badge variant={getMatchBadgeVariant(item.is_manual)} className="text-xs">
                                    {item.is_manual ? "Manual Match" : "Auto Match"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="py-4">
                                  <div className="space-y-1">
                                    <div className="text-sm text-gray-900">{item.match_reason}</div>
                                    <div className="text-xs text-gray-500">
                                      Score: {Math.round(item.match_score * 100)}%
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="py-4">
                                  <div className="text-sm text-gray-600">{item.is_manual ? "User" : "System"}</div>
                                  <div className="text-xs text-gray-500">
                                    {format(parseISO(item.deduction.created_at), "MMM d, yyyy")}
                                  </div>
                                </TableCell>
                                <TableCell className="py-4">
                                  <Button
                                    variant="outline"
                                    onClick={() => {
                                      window.open(`/deduction/${item.deduction.id}`, "_blank")
                                    }}>
                                    <ExternalLink className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* Search Results */}
                  {debouncedSearchTerm && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Search Results</h3>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-sm">
                            {searchResults.length} found
                          </Badge>
                          {searchLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                        </div>
                      </div>
                      {searchResults.length > 0 ? (
                        <div className="overflow-auto border border-gray-200 rounded-lg shadow-sm">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                <TableHead className="font-semibold text-gray-700 pl-6">Invoice</TableHead>
                                <TableHead className="font-semibold text-gray-700">Date</TableHead>
                                <TableHead className="font-semibold text-gray-700">Amount</TableHead>
                                <TableHead className="font-semibold text-gray-700">Customer / Retailer</TableHead>
                                <TableHead className="font-semibold text-gray-700">Category</TableHead>
                                <TableHead className="font-semibold text-gray-700">Status</TableHead>
                                <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {searchResults.map(deduction => {
                                const isMatched = matchedDeductionIds.has(deduction.id)
                                return (
                                  <TableRow
                                    key={deduction.id}
                                    className={`hover:bg-gray-50 ${isMatched ? "bg-gray-50" : ""}`}>
                                    <TableCell className="py-4 pl-6">
                                      <div className="space-y-1">
                                        <div className="font-medium text-gray-900">{deduction.invoice_number}</div>
                                        {deduction.description && (
                                          <div className="max-w-[220px] truncate text-xs text-gray-500" title={deduction.description}>
                                            {deduction.description}
                                          </div>
                                        )}
                                        {isMatched && (
                                          <Badge variant="secondary" className="text-xs">
                                            Already Matched
                                          </Badge>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-4">
                                      <span className="text-gray-900">
                                        {format(parseISO(deduction.invoice_date), "MMM d, yyyy")}
                                      </span>
                                    </TableCell>
                                    <TableCell className="py-4">
                                      <div className="space-y-1">
                                        <div className="font-medium text-gray-900">
                                          {exactCurrencyFormatter(Math.abs(deduction.invoice_amount))}
                                        </div>
                                        {deduction.discount_amount && (
                                          <div className="text-sm text-gray-500">
                                            Discount: {exactCurrencyFormatter(deduction.discount_amount)}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-4">
                                      <div className="space-y-0.5">
                                        <div className="text-gray-900">{deduction.customer_name || "Unknown Customer"}</div>
                                        {deduction.retailer_name && deduction.retailer_name !== deduction.customer_name && (
                                          <div className="text-xs text-gray-500">{deduction.retailer_name}</div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-4">
                                      {deduction.category ? (
                                        <div className="max-w-[200px]">
                                          <div className="truncate text-sm text-gray-900" title={deduction.category}>
                                            {deduction.category}
                                          </div>
                                          {deduction.category_description && (
                                            <div className="truncate text-xs text-gray-500" title={deduction.category_description}>
                                              {deduction.category_description}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-gray-400">Uncategorized</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="py-4">
                                      <Badge variant="outline" className="text-xs">
                                        {deduction.kind}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="py-4">
                                      <div className="flex gap-2">
                                        <Button
                                          variant="outline"
                                          onClick={() => {
                                            window.open(`/deduction/${deduction.id}`, "_blank")
                                          }}>
                                          <ExternalLink className="w-4 h-4 mr-1" />
                                          View
                                        </Button>
                                        {!isMatched && (
                                          <Button
                                            onClick={() => createMatch(deduction.id)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white">
                                            <Plus className="w-4 h-4 mr-1" />
                                            Match
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        !searchLoading && (
                          <div className="text-center text-gray-500 py-8">
                            <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p>No deductions found for "{debouncedSearchTerm}"</p>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
